import React, { useState } from 'react'
import { 
  Bold, 
  Italic, 
  Underline, 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  AlignJustify,
  Save,
  Download,
  Upload,
  FileText,
  Settings,
  Brain,
  Undo,
  Redo,
  Copy,
  Scissors,
  Clipboard,
  Paintbrush,
  Type,
  List,
  Table,
  Image,
  Link,
  BookOpen,
  Bookmark,
  Hash,
  Columns,
  Ruler,
  RotateCw,
  Maximize,
  Palette,
  Highlighter,
  Superscript,
  Subscript,
  Strikethrough,
  List as BulletList,
  ListOrdered,
  ChevronRight,
  ChevronLeft,
  Minus,
  Square,
  Type as FontColor,
  Paintbrush as FormatPainter,
  Eraser,
  Search,
  Replace,
  MousePointer,
  SortAsc,
  Filter,
  Shield,
  BarChart3,
  PieChart,
  Eye,
  GitCompare,
  MessageSquare,
  Shield as Protect,
  Share2,
  Eye as View,
  ZoomIn,
  Grid3X3,
  Ruler as RulerIcon,
  Navigation as NavigationIcon,
  Code,
  Puzzle,
  FileText as Header,
  FileText as Footer,
  Hash as PageNumber,
  BarChart3 as Chart,
  X,
  RotateCcw
} from 'lucide-react'
import { useDocument } from '../store/DocumentContext'

const RibbonMenu: React.FC = () => {
  const { state, dispatch } = useDocument()
  const [activeTab, setActiveTab] = useState('HOME')

  const tabs = [
    { id: 'HOME', label: 'HOME' },
    { id: 'INSERT', label: 'INSERT' },
    { id: 'PAGE_LAYOUT', label: 'PAGE LAYOUT' },
    { id: 'REFERENCES', label: 'REFERENCES' },
    { id: 'MAILINGS', label: 'MAILINGS' },
    { id: 'REVIEW', label: 'REVIEW' },
    { id: 'VIEW', label: 'VIEW' },
    { id: 'SETTINGS', label: 'SETTINGS' }
  ]

  const fontSizes = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72]
  const fontFamilies = [
    'Times New Roman',
    'Arial',
    'Calibri',
    'Georgia',
    'Verdana',
    'Helvetica',
    'Courier New'
  ]

  const renderHomeTab = () => (
    <div className="flex items-center gap-6 px-4 py-2">
      {/* Clipboard Group */}
      <div className="flex items-center gap-2 border-r border-gray-300 pr-4">
        <button className="p-2 hover:bg-gray-100 rounded" title="Undo">
          <Undo className="w-4 h-4" />
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Redo">
          <Redo className="w-4 h-4" />
        </button>
        <div className="border-l border-gray-300 h-6 mx-2"></div>
        <button className="p-2 hover:bg-gray-100 rounded" title="Cut">
          <Scissors className="w-4 h-4" />
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Copy">
          <Copy className="w-4 h-4" />
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Paste">
          <Clipboard className="w-4 h-4" />
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Format Painter">
          <Paintbrush className="w-4 h-4" />
        </button>
      </div>

      {/* Font Group */}
      <div className="flex items-center gap-2 border-r border-gray-300 pr-4">
        <select
          value={state.fontFamily}
          onChange={(e) => dispatch({ type: 'SET_FONT_FAMILY', payload: e.target.value })}
          className="px-2 py-1 border border-gray-300 rounded text-sm min-w-[120px]"
        >
          {fontFamilies.map(font => (
            <option key={font} value={font}>{font}</option>
          ))}
        </select>
        <select
          value={state.fontSize}
          onChange={(e) => dispatch({ type: 'SET_FONT_SIZE', payload: Number(e.target.value) })}
          className="px-2 py-1 border border-gray-300 rounded text-sm w-16"
        >
          {fontSizes.map(size => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>
        <button
          onClick={() => dispatch({ type: 'TOGGLE_BOLD' })}
          className={`p-2 rounded ${state.isBold ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          onClick={() => dispatch({ type: 'TOGGLE_ITALIC' })}
          className={`p-2 rounded ${state.isItalic ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          onClick={() => dispatch({ type: 'TOGGLE_UNDERLINE' })}
          className={`p-2 rounded ${state.isUnderline ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
          title="Underline"
        >
          <Underline className="w-4 h-4" />
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Strikethrough">
          <Strikethrough className="w-4 h-4" />
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Subscript">
          <Subscript className="w-4 h-4" />
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Superscript">
          <Superscript className="w-4 h-4" />
        </button>
        <div className="border-l border-gray-300 h-6 mx-2"></div>
        <button className="p-2 hover:bg-gray-100 rounded" title="Font Color">
          <FontColor className="w-4 h-4" />
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Highlight">
          <Highlighter className="w-4 h-4" />
        </button>
      </div>

      {/* Paragraph Group */}
      <div className="flex items-center gap-2 border-r border-gray-300 pr-4">
        <button
          onClick={() => dispatch({ type: 'SET_ALIGNMENT', payload: 'left' })}
          className={`p-2 rounded ${state.alignment === 'left' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
          title="Align Left"
        >
          <AlignLeft className="w-4 h-4" />
        </button>
        <button
          onClick={() => dispatch({ type: 'SET_ALIGNMENT', payload: 'center' })}
          className={`p-2 rounded ${state.alignment === 'center' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
          title="Align Center"
        >
          <AlignCenter className="w-4 h-4" />
        </button>
        <button
          onClick={() => dispatch({ type: 'SET_ALIGNMENT', payload: 'right' })}
          className={`p-2 rounded ${state.alignment === 'right' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
          title="Align Right"
        >
          <AlignRight className="w-4 h-4" />
        </button>
        <button
          onClick={() => dispatch({ type: 'SET_ALIGNMENT', payload: 'justify' })}
          className={`p-2 rounded ${state.alignment === 'justify' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
          title="Justify"
        >
          <AlignJustify className="w-4 h-4" />
        </button>
        <div className="border-l border-gray-300 h-6 mx-2"></div>
        <button className="p-2 hover:bg-gray-100 rounded" title="Bullet List">
          <BulletList className="w-4 h-4" />
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Numbered List">
          <ListOrdered className="w-4 h-4" />
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Decrease Indent">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Increase Indent">
          <ChevronRight className="w-4 h-4" />
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Line Spacing">
          <Minus className="w-4 h-4" />
        </button>
      </div>

      {/* Styles Group */}
      <div className="flex items-center gap-2 border-r border-gray-300 pr-4">
        <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
          Styles
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Clear Formatting">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Editing Group */}
      <div className="flex items-center gap-2">
        <button className="p-2 hover:bg-gray-100 rounded" title="Find">
          <Search className="w-4 h-4" />
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Replace">
          <Replace className="w-4 h-4" />
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Select">
          <MousePointer className="w-4 h-4" />
        </button>
      </div>
    </div>
  )

  const renderInsertTab = () => (
    <div className="flex items-center gap-6 px-4 py-2">
      {/* Pages Group */}
      <div className="flex items-center gap-2 border-r border-gray-300 pr-4">
        <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
          Page Break
        </button>
        <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
          Cover Page
        </button>
        <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
          Blank Page
        </button>
      </div>

      {/* Tables Group */}
      <div className="flex items-center gap-2 border-r border-gray-300 pr-4">
        <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
          Table
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Insert Table">
          <Table className="w-4 h-4" />
        </button>
      </div>

      {/* Illustrations Group */}
      <div className="flex items-center gap-2 border-r border-gray-300 pr-4">
        <button className="p-2 hover:bg-gray-100 rounded" title="Picture">
          <Image className="w-4 h-4" />
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Shapes">
          <Type className="w-4 h-4" />
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Chart">
          <Chart className="w-4 h-4" />
        </button>
      </div>

      {/* Links Group */}
      <div className="flex items-center gap-2 border-r border-gray-300 pr-4">
        <button className="p-2 hover:bg-gray-100 rounded" title="Hyperlink">
          <Link className="w-4 h-4" />
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Bookmark">
          <Bookmark className="w-4 h-4" />
        </button>
      </div>

      {/* Header & Footer Group */}
      <div className="flex items-center gap-2">
        <button className="p-2 hover:bg-gray-100 rounded" title="Header">
          <Header className="w-4 h-4" />
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Footer">
          <Footer className="w-4 h-4" />
        </button>
        <button className="p-2 hover:bg-gray-100 rounded" title="Page Number">
          <PageNumber className="w-4 h-4" />
        </button>
      </div>
    </div>
  )

  const renderPageLayoutTab = () => (
    <div className="flex items-center gap-6 px-4 py-2">
      {/* Page Setup Group */}
      <div className="flex items-center gap-2 border-r border-gray-300 pr-4">
        <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
          Margins
        </button>
        <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
          Orientation
        </button>
        <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
          Size
        </button>
        <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
          Columns
        </button>
      </div>

      {/* Paragraph Group */}
      <div className="flex items-center gap-2 border-r border-gray-300 pr-4">
        <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
          Indent
        </button>
        <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
          Spacing
        </button>
      </div>

      {/* Arrange Group */}
      <div className="flex items-center gap-2">
        <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
          Position
        </button>
        <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
          Wrap Text
        </button>
        <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
          Bring Forward
        </button>
        <button className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
          Send Backward
        </button>
      </div>
    </div>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case 'HOME':
        return renderHomeTab()
      case 'INSERT':
        return renderInsertTab()
      case 'PAGE_LAYOUT':
        return renderPageLayoutTab()
      default:
        return (
          <div className="px-4 py-2 text-gray-500">
            {activeTab} tab content coming soon...
          </div>
        )
    }
  }

  return (
    <div className="border-b border-gray-200">
      {/* Tab Navigation */}
      <div className="flex bg-gray-100 border-b border-gray-200">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-700 hover:bg-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white">
        {renderTabContent()}
      </div>
    </div>
  )
}

export default RibbonMenu 